/**
 * builder-palette.js — task #552
 *
 * Owns the whole primary-colour picker beyond the ten hard-coded swatches in
 * the nine builder pages:
 *
 *   Primary Color
 *   From your photo   ● ● ● ●        <- extracted from the header image
 *   Classic colors    ● ● ● … ● ⊕    <- the curated ten, plus a custom chip
 *
 * Three jobs:
 *
 * 1. Extract a palette from the header image and offer it as a labelled row
 *    ABOVE the curated one. The curated row is never modified; the photo row
 *    appears when there is something to offer and disappears when there isn't
 *    (no image, the stock default, or an image the browser won't let us read
 *    pixels from).
 * 2. Label the curated row, so the two rows read as two different offers
 *    rather than one long wrap of circles.
 * 3. Surface the native colour input as the last chip in the curated row —
 *    a rainbow "⊕" — instead of a lone square floating above the swatches
 *    where nobody found it.
 *
 * Deliberately self-contained and additive:
 * - The photo swatches are plain .color-swatch buttons inside the same
 *   .color-picker-container, so the delegated click handler in
 *   builder-preview.js treats them exactly like the curated ones — no
 *   listeners to rebind, and the selected ring moves across both rows.
 * - The custom chip carries .color-swatch too (so it gets the same size,
 *   hover and selected ring, and the handler's "clear every ring" sweep
 *   covers it) but NO data-color, which makes that same handler no-op on it.
 *   The real <input id="primaryColor"> is moved inside the chip and made
 *   transparent, so a click on the chip IS a click on the input — every
 *   existing listener and getElementById('primaryColor') keeps working.
 * - The host's chosen primary color is never touched — only the options on
 *   offer change.
 * - imageSrc is written from many places (gallery pick, upload, cropper,
 *   manual URL, form load), so rather than patching every one of them we
 *   watch builderOptions.imageSrc on a slow poll. One string compare every
 *   1.5s; extraction only runs when the value actually changes. The same
 *   tick re-syncs the custom chip, since form load sets the input's .value
 *   in code and fires no event.
 * - Envelope and seal swatch rows are untouched — only the #primaryColor
 *   container gains the extra row, the labels and the chip.
 */
(function () {
    'use strict';

    var POLL_MS = 1500;
    var SAMPLE_SIZE = 64;      // image downscaled to at most 64x64 for counting
    var BUCKET_BITS = 4;       // 4 bits/channel quantisation for the histogram
    var MIN_DISTANCE = 62;     // min RGB distance between offered swatches
    var MAX_PHOTO_SWATCHES = 8;
    var NEUTRAL_CHROMA = 0.12; // below this a colour reads as a grey/beige
    var MAX_NEUTRALS = 1;      // at most one neutral in the offered row
    var HUE_FAMILIES = 12;     // 30° bins — coarse enough to re-unite a rose
    var NEUTRAL_FAMILY = 'neutral';
    var lastSeenSrc = null;    // last imageSrc we processed (null = never ran)
    var defaultRow = null;     // the untouched curated .color-swatches row
    var photoWrap = null;      // wrapper holding the label + photo swatch row
    var classicLabel = null;   // label sitting above the curated row
    var customChip = null;     // the rainbow "custom colour" chip
    var lastChipColor = null;  // last value we painted onto the chip

    var LABELS = {
        en: 'From your photo', es: 'De tu foto', fr: 'De votre photo',
        de: 'Aus Ihrem Foto', pt: 'Da sua foto', he: 'מהתמונה שלכם', ar: 'من صورتكم'
    };

    var CLASSIC_LABELS = {
        en: 'Classic colors', es: 'Colores clásicos', fr: 'Couleurs classiques',
        de: 'Klassische Farben', pt: 'Cores clássicas', he: 'צבעים קלאסיים',
        ar: 'ألوان كلاسيكية'
    };

    var CUSTOM_LABELS = {
        en: 'Custom color', es: 'Color personalizado', fr: 'Couleur personnalisée',
        de: 'Eigene Farbe', pt: 'Cor personalizada', he: 'צבע מותאם אישית',
        ar: 'لون مخصص'
    };

    function pageLang() {
        // The localized builder pages set <html lang> (he/ar/es/fr/de/pt).
        return (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    }

    function label(map) {
        return map[pageLang()] || map.en;
    }

    function findDefaultRow() {
        var picker = document.getElementById('primaryColor');
        if (!picker) return false;
        var container = picker.closest('.color-picker-container');
        if (!container) return false;
        defaultRow = container.querySelector('.color-swatches');
        return !!defaultRow;
    }

    /* ---------------------------------------------------------------- CSS */

    function injectStyles() {
        if (document.getElementById('builderPaletteStyles')) return;
        var s = document.createElement('style');
        s.id = 'builderPaletteStyles';
        s.textContent = [
            '.palette-row-label{font-size:.78rem;color:#6b7280;margin-top:.5rem;}',
            // The chip inherits .color-swatch's size/border/ring from
            // builder.css (32px) and builder-v2.css (30px) — only the things
            // a <span> needs, and the rainbow fill, live here.
            // A rainbow RING, not a rainbow disc — a solid one would be the
            // loudest thing in a panel of muted swatches and would read as
            // an eleventh colour on offer rather than as "mix your own".
            //
            // (task #559) Three fixes over the original version:
            //  - The "+" is centred with flexbox, not absolute + translate(-50%).
            //    The translate reference is the padding box, which the 1–2px
            //    border shifts off the visual centre — flex centres against the
            //    rendered circle every time, at any swatch size.
            //  - background-clip:padding-box keeps the conic gradient INSIDE the
            //    border rather than painting under a semi-transparent border,
            //    which is what made the outer edge read as a squared-off ring.
            //  - (round 2) The "+" is DRAWN, not typed. Centring the span was
            //    never the problem: the span was centred to a hundredth of a
            //    pixel and the plus still sat low, because a text glyph's ink is
            //    not centred in its own line box — where it lands depends on the
            //    font's ascent and descent, and with line-height:1 that pushes a
            //    "+" below the middle. Measuring the element proved nothing
            //    about where the mark appeared. Two bars positioned from the
            //    span's own edges have no font in them at all, so the cross is
            //    exactly centred in every font, at every size, on every platform.
            //    The glyph stays in the DOM (font-size:0) so anything reading
            //    the chip's text still finds a "+".
            '.custom-color-swatch{display:inline-flex;align-items:center;justify-content:center;',
            'position:relative;overflow:hidden;box-sizing:border-box;',
            'background-clip:padding-box;background-origin:padding-box;',
            'background-image:radial-gradient(circle,#fff 0 44%,transparent 45%),',
            'conic-gradient(#e5534b,#e3a83c,#d8c93f,#5aa95a,#4a9bd1,#7b6fd0,#c968b0,#e5534b);}',
            // The real <input type="color"> sits invisibly on top, so the
            // chip opens the OS picker with no JS click-forwarding.
            '.custom-color-swatch input[type="color"]{position:absolute;top:0;left:0;',
            'width:100%;height:100%;min-width:0;min-height:0;margin:0;padding:0;border:0;',
            'opacity:0;cursor:pointer;background:transparent;}',
            '.custom-color-swatch .custom-color-plus{position:relative;display:block;',
            'width:9px;height:9px;pointer-events:none;font-size:0;line-height:0;color:transparent;}',
            '.custom-color-swatch .custom-color-plus::before,',
            '.custom-color-swatch .custom-color-plus::after{content:"";position:absolute;',
            'background:#4b5563;border-radius:1px;}',
            // Horizontal bar: spans the full width, centred on the vertical axis.
            '.custom-color-swatch .custom-color-plus::before{left:0;right:0;top:50%;',
            'height:2px;transform:translateY(-50%);}',
            // Vertical bar: spans the full height, centred on the horizontal axis.
            '.custom-color-swatch .custom-color-plus::after{top:0;bottom:0;left:50%;',
            'width:2px;transform:translateX(-50%);}',
            // The active custom swatch (task #560) — a normal solid swatch that
            // shows the colour the host mixed, sitting BEFORE the "+" chip. The
            // "+" chip stays a "+" forever so the picker is always one click away.
            '.custom-active-swatch{background-clip:padding-box;}'
        ].join('');
        document.head.appendChild(s);
    }

    /* -------------------------------------------------- the custom chip */

    /**
     * Move #primaryColor out of its lone wrapper and into the end of the
     * curated row, wrapped in a rainbow chip. The input keeps its id, its
     * value and every listener already bound to it.
     */
    function buildCustomChip() {
        var input = document.getElementById('primaryColor');
        if (!input || customChip) return;
        customChip = document.createElement('span');
        customChip.className = 'color-swatch custom-color-swatch';
        customChip.id = 'customColorChip';
        customChip.setAttribute('title', label(CUSTOM_LABELS));
        var plus = document.createElement('span');
        plus.className = 'custom-color-plus';
        plus.textContent = '+';
        var oldParent = input.parentNode;
        customChip.appendChild(input);
        customChip.appendChild(plus);
        defaultRow.appendChild(customChip);
        // The .d-flex that used to hold the input is now empty and would
        // otherwise leave a gap above the swatches.
        if (oldParent && oldParent !== defaultRow && !oldParent.children.length) {
            oldParent.style.display = 'none';
        }
        input.addEventListener('input', syncCustomChip);
        input.addEventListener('change', syncCustomChip);
    }

    // Whether a hex reads as light, so the ✓ can flip to dark on it.
    function isLightColor(hex) {
        var m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
        if (!m) return false;
        var n = parseInt(m[1], 16);
        var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
        // Rec. 601 luma; >0.7 of full is "light".
        return (0.299 * r + 0.587 * g + 0.114 * b) > 178;
    }

    function offeredColors() {
        var out = [];
        var picker = document.getElementById('primaryColor');
        var container = picker && picker.closest('.color-picker-container');
        if (!container) return out;
        container.querySelectorAll('.color-swatch[data-color]').forEach(function (b) {
            // The active custom swatch carries data-color too, but it IS the
            // custom colour — counting it here would make the code decide the
            // colour is "on offer" and tear the swatch back down (task #560).
            if (b.classList.contains('custom-active-swatch')) return;
            out.push((b.getAttribute('data-color') || '').toLowerCase());
        });
        return out;
    }

    /**
     * Keep the row honest about a host-mixed colour (tasks #552, #560).
     *
     * The rainbow "+" chip ALWAYS stays a "+": the picker is one click away at
     * all times. When the form's colour isn't one of the offered swatches, a
     * SEPARATE solid swatch — the "active custom swatch" — is shown just before
     * the chip, carrying the colour and the selected ring, so the row reflects
     * what the form is actually set to. When the colour goes back to an offered
     * swatch, that extra swatch is removed.
     */
    function syncCustomChip() {
        if (!customChip) return;
        var input = document.getElementById('primaryColor');
        var val = (input && input.value ? input.value : '').toLowerCase();
        var isCustom = !!val && offeredColors().indexOf(val) === -1;
        var existing = customChip.parentNode
            ? customChip.parentNode.querySelector('.custom-active-swatch') : null;

        // Cache key carries the swatch's live selected state too — the generic
        // swatch handler strips .selected off every .color-swatch on a click,
        // so a stale cache would leave the ring off after re-selecting.
        var key = val + '|' + isCustom + '|' + (existing ? existing.className : '-');
        if (key === lastChipColor) return;

        if (isCustom) {
            var swatch = existing;
            if (!swatch) {
                swatch = document.createElement('span');
                swatch.className = 'color-swatch custom-active-swatch';
                swatch.setAttribute('title', label(CUSTOM_LABELS));
                // Before the "+" chip, so the chip stays at the end of the row.
                customChip.parentNode.insertBefore(swatch, customChip);
            }
            swatch.setAttribute('data-color', val);   // so the generic handler re-selects it
            swatch.style.background = val;
            swatch.classList.add('selected');
            swatch.classList.toggle('light-color', isLightColor(val));
            // A colour mixed in the native picker fires no swatch click, so
            // nothing clears the ring off whichever swatch was chosen before —
            // the row would otherwise claim two active colours. Scoped to this
            // container so the seal and envelope rings are untouched.
            var container = customChip.closest('.color-picker-container');
            if (container) {
                container.querySelectorAll('.color-swatch[data-color]').forEach(function (s) {
                    if (s !== swatch) s.classList.remove('selected', 'light-color');
                });
            }
        } else if (existing) {
            // Round 2 of task #560: KEEP the swatch, just take the ring off it.
            // It used to be removed the moment a standard colour was picked, so
            // comparing a mixed colour against one of the ten meant losing it and
            // mixing it again from memory to get back. The swatch stays where it
            // is, unselected, and clicking it returns to that colour — the
            // generic swatch handler reads its data-color like any other.
            existing.classList.remove('selected');
        }
        // Re-read live state for the cache so a subsequent handler-driven ring
        // clear is noticed on the next tick.
        var now = customChip.parentNode
            ? customChip.parentNode.querySelector('.custom-active-swatch') : null;
        lastChipColor = val + '|' + isCustom + '|' + (now ? now.className : '-');
    }

    /* --------------------------------------------------- the photo row */

    /**
     * Show the photo palette as its own labelled row above the standard one.
     * Rebuilt on every image change; hidden via hidePhotoRow() when the
     * current image offers nothing.
     */
    function applyColors(colors) {
        if (!defaultRow) return;
        if (!photoWrap) {
            photoWrap = document.createElement('div');
            photoWrap.id = 'photoPaletteRow';
            var lab = document.createElement('div');
            lab.className = 'palette-row-label photo-palette-label';
            lab.textContent = label(LABELS);
            var row = document.createElement('div');
            row.className = 'color-swatches mt-1';
            photoWrap.appendChild(lab);
            photoWrap.appendChild(row);
            // Above the curated row AND above its label.
            defaultRow.parentNode.insertBefore(photoWrap, classicLabel || defaultRow);
        }
        var swatchRow = photoWrap.lastElementChild;
        swatchRow.innerHTML = '';
        colors.slice(0, MAX_PHOTO_SWATCHES).forEach(function (c) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'color-swatch';
            b.setAttribute('data-color', c);
            b.setAttribute('title', c);
            b.style.backgroundColor = c;
            swatchRow.appendChild(b);
        });
        photoWrap.style.display = '';
        syncCustomChip(); // the photo row changes what counts as "custom"
    }

    function hidePhotoRow() {
        if (photoWrap) photoWrap.style.display = 'none';
    }

    function toHex(n) { var s = n.toString(16); return s.length === 1 ? '0' + s : s; }

    function chromaOf(r, g, b) {
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        return (mx - mn) / 255;
    }

    function hueOf(r, g, b) {
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b), c = mx - mn;
        if (!c) return 0;
        var h;
        if (mx === r) h = ((g - b) / c) % 6;
        else if (mx === g) h = (b - r) / c + 2;
        else h = (r - g) / c + 4;
        return (h * 60 + 360) % 360;
    }

    /**
     * Histogram the downscaled image and pick up to `want` colors that are
     * (a) worth offering, (b) mutually distinct, (c) plausible as a PRIMARY
     * color — near-white/near-black pixels are skipped (backgrounds, borders,
     * text), and very light picks are gently darkened so white button text
     * stays readable on them.
     *
     * "Worth offering" is NOT the same as "most frequent", and it is not
     * bucket-by-bucket either. Both of those fail on real photographs:
     *
     * - Raw frequency hands back the wall, the tablecloth and skin tones.
     * - Ranking individual colour buckets — even weighted by chroma — loses to
     *   FRAGMENTATION. A rose in soft light spreads across a dozen buckets
     *   (highlight, midtone, shadow), each holding a fraction of the count,
     *   while a flat bokeh background piles into a few. On the stock header
     *   image the entire top-14 came back hue 31–44: all bokeh, no roses, no
     *   foliage, in a picture that is visibly pink and green.
     *
     * So buckets are grouped into HUE FAMILIES (12 bins of 30°, plus one
     * "neutral" family for anything under NEUTRAL_CHROMA), and a family's
     * weight is the chroma-weighted pixel count of everything in it — which
     * re-unites a fragmented rose. Selection then goes family by family,
     * largest first, taking one representative each (the member with the best
     * sqrt(count) × chroma, so a vivid and reasonably common member, not the
     * palest). That guarantees the row spans what the picture is actually
     * MADE of before it offers a second shade of anything. Further rounds
     * top the row up with additional members of the same families until it is
     * full or nothing distinct is left. Neutrals sort last and are capped, so
     * a muted photo still offers its one good grey without leading with it.
     */
    function pickPalette(data, want) {
        var buckets = Object.create(null);
        var shift = 8 - BUCKET_BITS;
        for (var p = 0; p < data.length; p += 4) {
            var a = data[p + 3];
            if (a < 200) continue; // transparent edge pixels
            var r = data[p], g = data[p + 1], b = data[p + 2];
            var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            // Skip page-white and true black only. Rich near-blacks stay in:
            // a black-tie invitation's paper IS its color, and the curated set
            // itself offers a #3C3C3C charcoal (task #552 follow-up).
            if (lum > 236 || lum < 10) continue;
            var key = (r >> shift) + ',' + (g >> shift) + ',' + (b >> shift);
            var bk = buckets[key];
            if (!bk) { bk = buckets[key] = { n: 0, r: 0, g: 0, b: 0 }; }
            bk.n++; bk.r += r; bk.g += g; bk.b += b;
        }
        var weight = Object.create(null);   // family key -> chroma-weighted count
        var members = Object.create(null);  // family key -> [candidate]
        Object.keys(buckets).forEach(function (k) {
            var bk = buckets[k];
            var r = bk.r / bk.n, g = bk.g / bk.n, b = bk.b / bk.n;
            var chroma = chromaOf(r, g, b);
            var isNeutral = chroma < NEUTRAL_CHROMA;
            var fam = isNeutral ? NEUTRAL_FAMILY
                : String(Math.floor(hueOf(r, g, b) / (360 / HUE_FAMILIES)));
            // Too pale to carry white text? Pull it toward its own hue,
            // darker. This happens BEFORE the distinctness check below,
            // because it pins every pale colour to the same luminance —
            // two picks that were 90 apart in RGB come out of it 4 apart
            // and land on the row as two identical-looking circles. Hue and
            // chroma are read from the ORIGINAL, which the scaling preserves
            // in direction but not in magnitude.
            var lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            if (lum > 190) { var f = 190 / lum; r *= f; g *= f; b *= f; }
            weight[fam] = (weight[fam] || 0) + bk.n * (isNeutral ? 1 : chroma);
            (members[fam] = members[fam] || []).push({
                r: r, g: g, b: b, used: false,
                rank: Math.sqrt(bk.n) * chroma
            });
        });

        // Biggest hue family first; the neutrals always bring up the rear so a
        // row never opens on a grey when the picture has real colour in it.
        var order = Object.keys(weight)
            .filter(function (k) { return k !== NEUTRAL_FAMILY; })
            .sort(function (a, b) { return weight[b] - weight[a]; });
        if (weight[NEUTRAL_FAMILY]) order.push(NEUTRAL_FAMILY);
        order.forEach(function (k) {
            members[k].sort(function (x, y) { return y.rank - x.rank; });
        });

        var picked = [];
        var neutrals = 0;
        function distinct(c) {
            return picked.every(function (q) {
                var dr = q.r - c.r, dg = q.g - c.g, db = q.b - c.b;
                return Math.sqrt(dr * dr + dg * dg + db * db) >= MIN_DISTANCE;
            });
        }
        var added = true;
        while (added && picked.length < want) {
            added = false;
            for (var i = 0; i < order.length && picked.length < want; i++) {
                var fam = order[i];
                if (fam === NEUTRAL_FAMILY && neutrals >= MAX_NEUTRALS) continue;
                var pool = members[fam];
                for (var j = 0; j < pool.length; j++) {
                    var c = pool[j];
                    if (c.used || !distinct(c)) continue;
                    c.used = true;
                    if (fam === NEUTRAL_FAMILY) neutrals++;
                    picked.push(c);
                    added = true;
                    break;
                }
            }
        }
        return picked.map(function (c) {
            return '#' + toHex(Math.round(c.r)) + toHex(Math.round(c.g)) + toHex(Math.round(c.b));
        });
    }

    function extractFrom(src, onDone) {
        var img = new Image();
        // Firebase Storage download URLs and same-origin gallery images both
        // serve CORS-readable pixels; anything that doesn't (a tainted canvas
        // throws on getImageData) simply shows no photo row.
        img.crossOrigin = 'anonymous';
        img.onload = function () {
            try {
                var w = img.naturalWidth, h = img.naturalHeight;
                if (!w || !h) return onDone(null);
                var scale = Math.min(1, SAMPLE_SIZE / Math.max(w, h));
                var cw = Math.max(1, Math.round(w * scale));
                var ch = Math.max(1, Math.round(h * scale));
                var canvas = document.createElement('canvas');
                canvas.width = cw; canvas.height = ch;
                var ctx = canvas.getContext('2d', { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, cw, ch);
                var data = ctx.getImageData(0, 0, cw, ch).data;
                var colors = pickPalette(data, MAX_PHOTO_SWATCHES);
                // However few the photo yields, they're worth offering — a
                // mostly-black invitation with one gold band should still
                // offer that black and that gold (task #552 follow-up). The
                // standard row sits right below either way.
                onDone(colors.length >= 1 ? colors : null);
            } catch (e) {
                onDone(null); // tainted canvas / CORS
            }
        };
        img.onerror = function () { onDone(null); };
        img.src = src;
    }

    function isUsableImage(src) {
        if (!src || typeof src !== 'string') return false;
        // The stock default used to be skipped here as "not really the host's
        // photo" — but it's the image most forms actually ship with, every
        // OTHER gallery image already gets a palette, and its blush/sage/cream
        // is exactly what the curated row is trying to approximate. Skipping it
        // meant the majority of hosts never saw the feature at all.
        return /^(https?:|data:|blob:|\/)/.test(src);
    }

    function tick() {
        // Form load and the swatch handler both set input.value in code,
        // which fires no event — so the chip is re-checked every tick.
        syncCustomChip();
        var src = window.builderCore && window.builderCore.builderOptions
            ? (window.builderCore.builderOptions.imageSrc || '') : '';
        if (src === lastSeenSrc) return;
        lastSeenSrc = src;
        if (!isUsableImage(src)) { hidePhotoRow(); return; }
        extractFrom(src, function (colors) {
            // The image may have changed again while we were reading this one.
            if (lastSeenSrc !== src) return;
            if (colors) applyColors(colors);
            else hidePhotoRow();
        });
    }

    document.addEventListener('DOMContentLoaded', function () {
        if (!findDefaultRow()) return; // page has no primary swatch row
        injectStyles();
        classicLabel = document.createElement('div');
        classicLabel.className = 'palette-row-label';
        classicLabel.id = 'classicPaletteLabel';
        classicLabel.textContent = label(CLASSIC_LABELS);
        defaultRow.parentNode.insertBefore(classicLabel, defaultRow);
        buildCustomChip();
        // First tick runs immediately so a form loaded with an image gets its
        // palette without waiting for the interval.
        setTimeout(tick, 800);
        setInterval(tick, POLL_MS);
    });
})();
